import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/utils/api-client';
import { Listing, ListingForm } from '@/types';

// Query keys
export const listingKeys = {
  all: ['listings'] as const,
  lists: () => [...listingKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...listingKeys.lists(), { filters }] as const,
  details: () => [...listingKeys.all, 'detail'] as const,
  detail: (id: string) => [...listingKeys.details(), id] as const,
};

// Get all user listings
export const useListings = (filters?: { status?: string }) => {
  return useQuery({
    queryKey: listingKeys.list(filters || {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) {
        params.append('status', filters.status);
      }
      
      const endpoint = `/listings${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get<Listing[]>(endpoint);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get single listing
export const useListing = (id: string) => {
  const isValidId = Boolean(id && id.trim() !== '' && id !== 'undefined' && id !== 'null')
  
  return useQuery({
    queryKey: listingKeys.detail(id),
    queryFn: async () => {
      if (!isValidId) {
        throw new Error('Invalid listing ID provided')
      }
      const response = await apiClient.get<Listing>(`/listings/${id}`);
      return response.data;
    },
    enabled: isValidId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Create new listing
export const useCreateListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ListingForm) => {
      const response = await apiClient.post<Listing>('/listings', data);
      return response.data;
    },
    onSuccess: (newListing) => {
      // Add to the listings cache
      queryClient.setQueryData<Listing[]>(
        listingKeys.list({}),
        (old) => old ? [newListing, ...old] : [newListing]
      );
      
      // Set individual listing cache
      queryClient.setQueryData(listingKeys.detail(newListing.listingId), newListing);
    },
    onSettled: () => {
      // Invalidate listings queries
      queryClient.invalidateQueries({ queryKey: listingKeys.lists() });
    },
  });
};

// Update listing
export const useUpdateListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ListingForm> }) => {
      const response = await apiClient.put<Listing>(`/listings/${id}`, data);
      return response.data;
    },
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: listingKeys.detail(id) });

      // Snapshot the previous value
      const previousListing = queryClient.getQueryData<Listing>(listingKeys.detail(id));

      // Optimistically update to the new value
      if (previousListing) {
        const updatedListing = {
          ...previousListing,
          ...data,
          updatedAt: new Date().toISOString(),
        };
        queryClient.setQueryData<Listing>(listingKeys.detail(id), updatedListing);
        
        // Update in listings list as well
        queryClient.setQueryData<Listing[]>(
          listingKeys.list({}),
          (old) => old ? old.map(listing => 
            listing.listingId === id ? updatedListing : listing
          ) : []
        );
      }

      return { previousListing };
    },
    onError: (err, { id }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousListing) {
        queryClient.setQueryData(listingKeys.detail(id), context.previousListing);
      }
    },
    onSettled: (data, error, { id }) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: listingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: listingKeys.lists() });
    },
  });
};

// Delete listing
export const useDeleteListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/listings/${id}`);
      return id;
    },
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: listingKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: listingKeys.lists() });

      // Snapshot the previous values
      const previousListing = queryClient.getQueryData<Listing>(listingKeys.detail(id));
      const previousListings = queryClient.getQueryData<Listing[]>(listingKeys.list({}));

      // Optimistically remove from cache
      queryClient.removeQueries({ queryKey: listingKeys.detail(id) });
      queryClient.setQueryData<Listing[]>(
        listingKeys.list({}),
        (old) => old ? old.filter(listing => listing.listingId !== id) : []
      );

      return { previousListing, previousListings };
    },
    onError: (err, id, context) => {
      // If the mutation fails, restore the previous values
      if (context?.previousListing) {
        queryClient.setQueryData(listingKeys.detail(id), context.previousListing);
      }
      if (context?.previousListings) {
        queryClient.setQueryData(listingKeys.list({}), context.previousListings);
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: listingKeys.lists() });
    },
  });
};

// Upload listing photos
export const useUploadListingPhotos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ listingId, files }: { listingId: string; files: File[] }) => {
      const uploadPromises = files.map(async (file) => {
        // Get presigned URL
        const urlResponse = await apiClient.post<{ uploadUrl: string; photoUrl: string; uploadId: string }>(`/listings/${listingId}/photos/upload`, {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        });

        // Upload to S3
        await fetch(urlResponse.data.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        return {
          photoUrl: urlResponse.data.photoUrl,
          uploadId: urlResponse.data.uploadId,
        };
      });

      const uploadResults = await Promise.all(uploadPromises);

      // Confirm uploads one by one
      let updatedListing: Listing | null = null;
      for (const result of uploadResults) {
        const confirmResponse = await apiClient.post<Listing>(`/listings/${listingId}/photos/confirm`, {
          uploadId: result.uploadId,
          photoUrl: result.photoUrl,
        });
        updatedListing = confirmResponse.data;
      }

      return updatedListing!;
    },
    onSuccess: (updatedListing) => {
      // Update the listing cache with the new photos
      queryClient.setQueryData<Listing>(listingKeys.detail(updatedListing.listingId), updatedListing);
      
      // Update in listings list as well
      queryClient.setQueryData<Listing[]>(
        listingKeys.list({}),
        (old) => old ? old.map(listing => 
          listing.listingId === updatedListing.listingId ? updatedListing : listing
        ) : []
      );
    },
    onSettled: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: listingKeys.detail(data.listingId) });
        queryClient.invalidateQueries({ queryKey: listingKeys.lists() });
      }
    },
  });
};